/**
 * Gallery Core Store - Layout, orientation, and artwork selection
 */

import { GALLERY_CONFIG } from '../gallery-config.js';
import { GalleryVariantManager } from '../managers/gallery-variant-manager.js';
import { formatPrice, formatFromPrice, getMinimumPrice } from '../services/pricing/price-utils.js';
import { createImageUrlManager, IMAGE_SIZES } from '../services/image/reactive-image-urls.js';

export function createGalleryCoreStore() {
  return {
    layout_count: GALLERY_CONFIG.LAYOUT.DEFAULT_LAYOUT_COUNT,
    orientation: GALLERY_CONFIG.LAYOUT.DEFAULT_ORIENTATION,
    selected_artworks: [],
    gallery_visible: true,
    third_artwork_visible: false,

    orientation_states: {
      horizontal: {
        selected_artworks: [],
        layout: GALLERY_CONFIG.LAYOUT.DEFAULT_LAYOUT_COUNT,
        saved_third_artwork: undefined
      },
      vertical: {
        selected_artworks: [],
        layout: GALLERY_CONFIG.LAYOUT.DEFAULT_LAYOUT_COUNT,
        saved_third_artwork: undefined
      }
    },

    // Unified variant selection state - single source of truth
    variant_selection: {
      size: null,
      frame: null,
      mount: null,
      locked: false
    },

    preview_variants: [],
    preview_loading: false,
    preview_loaded_images: new Set(),

    preload_cache: new Map(),
    preload_in_progress: new Map(),

    variant_manager: null,

    variant_lookup_cache: new Map(),
    cache_access_order: [],
    MAX_CACHE_SIZE: 100,
    cache_hits: 0,
    cache_misses: 0,

    IMAGE_SIZES: IMAGE_SIZES,

    _variantUpdateTimeout: null,
    _lastArtworkCount: 0,
    _lastArtworkIds: '',
    _lastLazyObservationKey: '',
    _performanceMetrics: null,
    _autoSaveEnabled: true,

    get layout_type() {
      return this.layout_count === 3 ? 'trio' : 'duo';
    },

    get layout_css_class() {
      return `gallery-layout--${this.layout_count}`;
    },

    get set_type_display_mobile() {
      const orientation = this.orientation?.toUpperCase() || 'HORIZONTAL';
      const layout = this.layout_count === 3 ? 'TRIO' : 'DUO';
      return `${orientation} ${layout} SET`;
    },

    get layout_icon_name() {
      return `${this.layout_type}-${this.orientation}`;
    },

    get layout_display_name() {
      return this.layout_count === 3 ? 'TRIO' : 'DUO';
    },

    get required_artwork_count() {
      return this.layout_count;
    },

    get current_orientation_artworks() {
      return this.orientation_states[this.orientation].selected_artworks;
    },

    get current_orientation_layout() {
      return this.orientation_states[this.orientation].layout;
    },

    get selected_artworks() {
      return this.current_orientation_artworks;
    },

    set selected_artworks(value) {
      const orientation_state = this.orientation_states[this.orientation];

      orientation_state.selected_artworks = value;

      if (Array.isArray(value) && value.length > 2) {
        this.setSavedThirdArtwork(value[2] ?? null);
      }
    },

    setSavedThirdArtwork(artwork, orientation = this.orientation) {
      if (!this.orientation_states[orientation]) {
        return;
      }

      this.orientation_states[orientation].saved_third_artwork = artwork;
    },

    getSavedThirdArtwork(orientation = this.orientation) {
      if (!this.orientation_states[orientation]) {
        return undefined;
      }

      return this.orientation_states[orientation].saved_third_artwork;
    },

    get gallery_complete() {
      const ui_store = Alpine.store('galleryUI');
      const valid_artworks_count = this.selected_artworks.filter(artwork => artwork && artwork.id).length;

      if (ui_store?.transitioning_layout) {
        return valid_artworks_count >= this.required_artwork_count;
      }
      return valid_artworks_count >= this.required_artwork_count;
    },

    get min_price_display() {
      if (this.selected_artworks.length === 0) return '';

      const total_cheapest_price = this.selected_artworks.reduce((total, artwork) => {
        if (!artwork || typeof artwork !== 'object') {
          return total;
        }

        if (!artwork.variants || artwork.variants.length === 0) {
          return total + (artwork.base_price || 0);
        }

        const cheapest_price = getMinimumPrice(artwork.variants);
        return total + cheapest_price;
      }, 0);

      return formatFromPrice(total_cheapest_price);
    },

    get computed_total_price() {
      if (!this.variant_selection.size || this.selected_artworks.length === 0) {
        return 0;
      }

      if (!this.variant_manager) {
        this.initVariantManager();
      }

      return this.variant_manager.calculatePrice(
        this.selected_artworks.filter(Boolean),
        this.variant_selection.size,
        this.variant_selection.frame,
        this.variant_selection.mount
      );
    },

    get total_price_display() {
      const price = this.computed_total_price;
      return price > 0 ? formatPrice(price) : '$0';
    },

    get can_add_to_cart() {
      return this.gallery_complete &&
             this.variant_selection.size !== null &&
             this.variant_selection.frame !== null &&
             this.variant_selection.mount !== null;
    },

    init() {
      Object.assign(this, createImageUrlManager());

      if (typeof Alpine !== 'undefined' && typeof Alpine.nextTick === 'function' && this.refreshLazyImageTargets) {
        Alpine.nextTick(() => {
          this.refreshLazyImageTargets();
        });
      }

      this._performanceMetrics = {
        variant_updates: 0,
        image_loads: 0,
        cache_clears: 0,
        last_update: Date.now()
      };

      if (this.initImageUrlWatchers) {
        this.initImageUrlWatchers();
      }

      this.setupReactiveWatchers();
      this.setupOrientationAutoSave();

    },

    setupReactiveWatchers() {
      Alpine.effect(() => {
        const { size, frame, mount } = this.variant_selection;

        if (!this.variant_manager) {
          return;
        }

        this._performanceMetrics.variant_updates++;

        requestAnimationFrame(() => {
          document.dispatchEvent(new CustomEvent('gallery-variant-changed', {
            detail: { size, frame, mount, locked: this.variant_selection.locked }
          }));
        });

        if (frame || mount) {
          clearTimeout(this._variantUpdateTimeout);
          this._variantUpdateTimeout = setTimeout(() => {
            if (this.selected_artworks.length > 0) {
              Alpine.nextTick(() => {
                this.updatePreviewVariants(frame ? 'frame' : 'mount');
              });
            }
          }, 100);
        }
      });

      Alpine.effect(() => {
        const artworkIds = this.selected_artworks
          .filter(a => a && a.id)
          .map(a => a.id)
          .join(',');
        const orientation = this.orientation || 'horizontal';
        const lazyKey = `${orientation}|${artworkIds}`;

        if (this._lastArtworkIds !== artworkIds) {
          if (this._lastArtworkIds && this.variant_lookup_cache.size > 0) {
            this.invalidateCacheForArtworks(this._lastArtworkIds, artworkIds);
            this._performanceMetrics.cache_clears++;
          }
          this._lastArtworkIds = artworkIds;
        }

        if (this.refreshLazyImageTargets && this._lastLazyObservationKey !== lazyKey) {
          this.refreshLazyImageTargets();
          this._lastLazyObservationKey = lazyKey;
        }
      });
    },

    initVariantManager() {
      if (!this.variant_manager) {
        this.variant_manager = new GalleryVariantManager();
      }
    },

    selectArtworks(artworks) {
      this.selected_artworks = artworks;

      // Only apply default variants if:
      // 1. We have artworks
      // 2. No variant configuration exists
      // 3. The artworks don't already have selected_variant set (not already processed)
      if (artworks && artworks.length > 0) {
        const hasVariantConfig = this.variant_selection.size || this.variant_selection.frame || this.variant_selection.mount;
        const needsVariantApplication = artworks.some(artwork => artwork && !artwork.selected_variant);

        if (!hasVariantConfig && needsVariantApplication) {
          this.variant_selection.size = 'Exhibition';
          this.variant_selection.frame = 'Black';
          this.variant_selection.mount = 'Border';
          this.variant_selection.locked = false;

          const variants_store = Alpine.store('galleryVariants');
          if (variants_store) {
            this.initVariantManager();
            artworks.forEach((artwork, index) => {
              if (artwork && !artwork.selected_variant) {
                variants_store.applyPreviewVariantToArtwork(artwork, index);
              }
            });
          }
        }
      }
    },

    selectArtwork(artwork, position) {
      const selection_store = Alpine.store('gallerySelection');
      return selection_store.selectArtwork(artwork, position);
    },

    async switchLayout(new_count) {
      const layout_store = Alpine.store('galleryLayout');
      return await layout_store.switchLayout(new_count);
    },

    setupOrientationAutoSave() {
      Alpine.effect(() => {
        const currentLayout = this.layout_count;
        const currentOrientation = this.orientation;
        const artworkCount = this.selected_artworks.filter(Boolean).length;

        if (currentOrientation && this._autoSaveEnabled) {
          this.orientation_states[currentOrientation].layout = currentLayout;

        }
      });
    },

    setAutoSaveEnabled(enabled) {
      this._autoSaveEnabled = enabled;
    },

    switchToOrientation(orientation, saved_config = null) {
      const current_artworks = this.selected_artworks;
      const current_variant_config = saved_config || { ...this.variant_selection };
      const current_layout_count = this.layout_count;

      this.orientation = orientation;

      const stored_artworks = this.current_orientation_artworks;

      if (stored_artworks.length === 0 && current_artworks.length > 0) {
        this.selected_artworks = current_artworks;
      }

      this.layout_count = current_layout_count;
      this.orientation_states.horizontal.layout = current_layout_count;
      this.orientation_states.vertical.layout = current_layout_count;

      // Only set third_artwork_visible if we're in trio mode AND have 3 valid artworks
      const validArtworkCount = this.selected_artworks.filter(artwork => artwork && artwork.id).length;
      this.third_artwork_visible = this.layout_count === 3 && validArtworkCount >= 3;

      if (this.layout_count === 3 && validArtworkCount < 3) {
        const saved_third = this.getSavedThirdArtwork(orientation);
        if (saved_third && validArtworkCount === 2) {
          this.selected_artworks = [...this.selected_artworks.slice(0, 2), saved_third];
          this.third_artwork_visible = true;
        } else {
          this.third_artwork_visible = false;
        }
      }

      if (current_variant_config) {
        Object.assign(this.variant_selection, {
          size: current_variant_config.size || null,
          frame: current_variant_config.frame || null,
          mount: current_variant_config.mount || null
        });

        if (this.selected_artworks.length > 0 && (current_variant_config.frame || current_variant_config.mount || current_variant_config.size)) {
          this.initVariantManager();
          this.selected_artworks.forEach((artwork, index) => {
            if (artwork) {
              const applyMethod = current_variant_config.locked ? 'applyVariantToArtwork' : 'applyPreviewVariantToArtwork';
              this[applyMethod](artwork, index);
            }
          });
        }
      }
    },

    clearAllOrientations() {
      this.orientation_states.horizontal.selected_artworks = [];
      this.orientation_states.vertical.selected_artworks = [];
      this.orientation_states.horizontal.layout = GALLERY_CONFIG.LAYOUT.DEFAULT_LAYOUT_COUNT;
      this.orientation_states.vertical.layout = GALLERY_CONFIG.LAYOUT.DEFAULT_LAYOUT_COUNT;
      this.orientation_states.horizontal.saved_third_artwork = undefined;
      this.orientation_states.vertical.saved_third_artwork = undefined;
      this.third_artwork_visible = false;
    },

    copyToOtherOrientation() {
      const target = this.orientation === 'horizontal' ? 'vertical' : 'horizontal';
      this.orientation_states[target].selected_artworks = [...this.selected_artworks];
      this.orientation_states[target].layout = this.layout_count;
    },

    resetCurrentOrientation() {
      this.selected_artworks = [];
      this.layout_count = GALLERY_CONFIG.LAYOUT.DEFAULT_LAYOUT_COUNT;
      this.third_artwork_visible = false;
    },

    resetVariantState(preserve_config = false) {
      this.variant_manager = null;

      if (!preserve_config) {
        this.variant_selection = {
          size: null,
          frame: null,
          mount: null,
          locked: false
        };
        this.preview_variants = [];
        this.preview_loading = false;

      } else {
        this.preview_variants = [];
        this.preview_loading = false;
      }
    },

    setVariantOption(type, value, options = {}) {
      const variants_store = Alpine.store('galleryVariants');
      return variants_store.setVariantOption(type, value, options);
    },

    findVariantForArtwork(artwork, config = null) {
      const variants_store = Alpine.store('galleryVariants');
      return variants_store.findVariantForArtwork(artwork, config);
    },

    applyPreviewVariantToArtwork(artwork, position) {
      const variants_store = Alpine.store('galleryVariants');
      return variants_store.applyPreviewVariantToArtwork(artwork, position);
    },

    setDefaultVariantForArtwork(artwork) {
      const variants_store = Alpine.store('galleryVariants');
      return variants_store.setDefaultVariantForArtwork(artwork);
    },

    async applyBulkVariantConfig(config) {
      const variants_store = Alpine.store('galleryVariants');
      return await variants_store.applyBulkVariantConfig(config);
    },

    applyVariantToArtwork(artwork, position) {
      const variants_store = Alpine.store('galleryVariants');
      return variants_store.applyVariantToArtwork(artwork, position);
    },

    async updatePreviewVariants(changed_option) {
      const variants_store = Alpine.store('galleryVariants');
      return await variants_store.updatePreviewVariants(changed_option);
    },

    formatPrice(price) {
      const variants_store = Alpine.store('galleryVariants');
      return variants_store.formatPrice(price);
    },

    prepareCartPayload() {
      const variants_store = Alpine.store('galleryVariants');
      return variants_store.prepareCartPayload();
    },

    prepareArtworksWithVariants(artworks, saved_config) {
      const variants_store = Alpine.store('galleryVariants');
      return variants_store.prepareArtworksWithVariants(artworks, saved_config);
    },

    applyVariantsToRestoredArtworks(saved_config) {
      const variants_store = Alpine.store('galleryVariants');
      return variants_store.applyVariantsToRestoredArtworks(saved_config);
    },

    isImagePreloaded(url, orientation = null) {
      const memory_store = Alpine.store('galleryMemory');
      return memory_store.isImagePreloaded(url, orientation);
    },

    async preloadImageBatch(urls, orientation = null) {
      const memory_store = Alpine.store('galleryMemory');
      return await memory_store.preloadImageBatch(urls, orientation);
    },

    async waitForImages(load_promise) {
      const memory_store = Alpine.store('galleryMemory');
      return await memory_store.waitForImages(load_promise);
    },

    updateCacheAccessOrder(key) {
      const memory_store = Alpine.store('galleryMemory');
      return memory_store.updateCacheAccessOrder(key);
    },

    clearOldestCacheEntriesIfNeeded() {
      const memory_store = Alpine.store('galleryMemory');
      return memory_store.clearOldestCacheEntriesIfNeeded();
    },

    invalidateCacheForArtworks(oldIds, newIds) {
      const memory_store = Alpine.store('galleryMemory');
      return memory_store.invalidateCacheForArtworks(oldIds, newIds);
    },

    getCacheMetrics() {
      const memory_store = Alpine.store('galleryMemory');
      return memory_store.getCacheMetrics();
    },

    clearCache() {
      const memory_store = Alpine.store('galleryMemory');
      return memory_store.clearCache();
    },

    getPerformanceReport() {
      const memory_store = Alpine.store('galleryMemory');
      return memory_store.getPerformanceReport();
    }
  };
}
